import { useEffect } from "react";
import { ANALYTICS, CANONICAL_ORIGIN, CONTACT, SITE, VERIFICATION } from "@/config/site.config";
import { OFFICE, SOCIALS } from "@/data/site";
import { ORG_ID, WEBSITE_ID } from "@/lib/seo";

/**
 * Site-wide head material that is identical on every page:
 *   • Search engine ownership-verification tags
 *   • Organization + WebSite structured data
 *   • Analytics, loaded lazily and only when configured
 *
 * All of it is driven by `src/config/site.config.js`, so pointing the site at a new
 * domain or adding a Search Console token is a one-line change with no code edits.
 *
 * Rendered once from AppShell. The build-time prerenderer snapshots the DOM after this
 * runs, so crawlers receive the verification tag and organization schema in the initial
 * HTML of every route rather than after hydration.
 */

const upsertMeta = (name, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

/** Organization + WebSite graph. Everything page-level references these by @id. */
const buildGraph = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ProfessionalService",
      "@id": ORG_ID,
      name: SITE.legalName,
      alternateName: SITE.name,
      url: CANONICAL_ORIGIN,
      logo: `${CANONICAL_ORIGIN}${SITE.logoPath}`,
      image: `${CANONICAL_ORIGIN}${SITE.logoPath}`,
      description:
        "Product + services company. Creators of EduAnant, India's school operating system, and SNAPXOps, job and route-card tracking for tool rooms. Web, mobile, software, UI/UX, branding, motion, digital marketing and finance/GST services.",
      email: CONTACT.email,
      telephone: CONTACT.phoneE164,
      foundingDate: SITE.foundingDate,
      priceRange: "$$",
      address: { "@type": "PostalAddress", ...OFFICE.postal },
      areaServed: [
        { "@type": "Country", name: "India" },
        { "@type": "Country", name: "United Arab Emirates" },
      ],
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "08:00",
          closes: "16:00",
        },
        { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "08:00", closes: "13:00" },
      ],
      // WhatsApp is a contact channel, not a public profile — excluded from sameAs.
      sameAs: SOCIALS.filter((s) => s.name !== "WhatsApp").map((s) => s.url),
    },
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      name: SITE.name,
      url: CANONICAL_ORIGIN,
      inLanguage: SITE.locale,
      publisher: { "@id": ORG_ID },
    },
  ],
});

/** Append a script tag once, keyed so re-renders never duplicate it. */
const loadScript = (key, attrs, inline) => {
  if (document.querySelector(`script[data-analytics="${key}"]`)) return;
  const el = document.createElement("script");
  el.setAttribute("data-analytics", key);
  el.async = true;
  Object.entries(attrs || {}).forEach(([k, v]) => el.setAttribute(k, v));
  if (inline) el.text = inline;
  document.head.appendChild(el);
};

const startAnalytics = () => {
  // Do Not Track is a stated preference; honour it before loading any tracker.
  if (ANALYTICS.respectDoNotTrack) {
    const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
    if (dnt === "1" || dnt === "yes") return;
  }

  if (ANALYTICS.ga4) {
    loadScript("ga4-src", { src: `https://www.googletagmanager.com/gtag/js?id=${ANALYTICS.ga4}` });
    loadScript(
      "ga4-init",
      {},
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ANALYTICS.ga4}');`,
    );
  }

  if (ANALYTICS.gtm) {
    loadScript(
      "gtm",
      {},
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${ANALYTICS.gtm}');`,
    );
  }

  if (ANALYTICS.plausibleDomain) {
    loadScript("plausible", {
      src: "https://plausible.io/js/script.js",
      "data-domain": ANALYTICS.plausibleDomain,
    });
  }
};

const SiteMeta = () => {
  useEffect(() => {
    upsertMeta("google-site-verification", VERIFICATION.google);
    upsertMeta("msvalidate.01", VERIFICATION.bing);
    upsertMeta("p:domain_verify", VERIFICATION.pinterest);
    upsertMeta("facebook-domain-verification", VERIFICATION.facebookDomain);

    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", "site");
    el.textContent = JSON.stringify(buildGraph());
    document.head.appendChild(el);

    return () => el.remove();
  }, []);

  useEffect(() => {
    // Deferred to idle so trackers never compete with first paint.
    const start = () => startAnalytics();
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(start, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(start, 2000);
    return () => clearTimeout(t);
  }, []);

  return null;
};

export default SiteMeta;
